import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { SimpleGrid, VStack } from '@chakra-ui/react';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import { formatAmount } from 'common/formatAmount';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import Text from 'components/_core/Text';
import { TeamMemberRequestStatusBadge } from 'components/TeamMemberRequestStatusBadge';
import { TeamRequestStatusToRequestsFilterState } from 'features/Team/pages/Requests/utils';
import { formatDate } from 'helpers/date';

import * as routes from '../../../../../../routes';
import { ExpenseClaimRequestFragment } from '../../../../graphql/ExpenseClaimRequestFragment.gql';
import locales from './locales';

interface ExpenseClaimRequestRowProps {
  isActive: boolean;
  request: ExpenseClaimRequestFragment;
  onClick: () => void;
}

export const ExpenseClaimRequestRow: FC<ExpenseClaimRequestRowProps> = ({
  isActive,
  onClick,
  request,
}) => {
  const recipientName = request.requester
    ? `${request.requester.firstName} ${request.requester.lastName}`
    : '';

  return (
    <SunshineCard
      alignItems="center"
      as={Link}
      isActive={isActive}
      onClick={onClick}
      to={routes.getRequestPath(
        request.teamMembersRequestId,
        TeamRequestStatusToRequestsFilterState[request.status],
      )}
    >
      <SunshineCard.Slot name="aside">
        <SquircleIcon
          name="receipt"
          sx={{ '& svg': { boxSize: 'space-16' } }}
        />
      </SunshineCard.Slot>
      <SimpleGrid
        alignItems="center"
        columns={3}
        spacing="space-24"
        templateColumns="6fr 3fr 3fr"
        width="100%"
      >
        <VStack alignItems="flex-start" spacing="space-0">
          <SunshineCard.Title>{locales.label}</SunshineCard.Title>
          <SunshineCard.Text>
            {request.status === TeamMembersRequestStatus.Pending
              ? locales.formatString(locales.descriptionWithDate, {
                  date: formatDate(new Date(request.createdAt), 'PP'),
                  name: recipientName,
                })
              : locales.formatString(locales.description, {
                  name: recipientName,
                })}
          </SunshineCard.Text>
        </VStack>
        <TeamMemberRequestStatusBadge
          from="account-holder"
          justifySelf="center"
          request={request}
        />
        <Text
          fontWeight={500}
          sx={{
            fontFeatureSettings: 'tnum',
            fontVariantNumeric: 'tabular-nums',
          }}
          textAlign="right"
          variant="primary"
        >
          {formatAmount({
            forcePlusSign: false,
            value: request.expenseClaimRequesterPayload.amount.total / 100,
            withNoMinorUnitDigits: true,
          })}
        </Text>
      </SimpleGrid>
    </SunshineCard>
  );
};
